import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Plus, CalendarDays, Clock, UserCheck, UserX } from "lucide-react";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AttendancePage = () => {
  const [attendance, setAttendance] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [formData, setFormData] = useState({
    employee_id: "",
    employee_name: "",
    date: new Date().toISOString().split('T')[0],
    check_in: "09:00",
    check_out: "18:00",
    status: "present",
    leave_type: "",
    remarks: ""
  });

  useEffect(() => {
    fetchData();
  }, [selectedDate]);

  const fetchData = async () => {
    try {
      const [attendanceRes, employeesRes] = await Promise.all([
        axios.get(`${API_URL}/attendance?date=${selectedDate}`),
        axios.get(`${API_URL}/employees`)
      ]);
      setAttendance(attendanceRes.data);
      setEmployees(employeesRes.data.filter(e => e.status === "active"));
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = { ...formData };
      if (submitData.status !== "leave") {
        delete submitData.leave_type;
      }
      
      await axios.post(`${API_URL}/attendance`, submitData);
      toast.success("Attendance marked successfully");
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to mark attendance");
    }
  };

  const resetForm = () => {
    setFormData({
      employee_id: "",
      employee_name: "",
      date: selectedDate,
      check_in: "09:00",
      check_out: "18:00",
      status: "present",
      leave_type: "",
      remarks: ""
    });
  };

  const handleEmployeeChange = (employeeId) => {
    const employee = employees.find(e => e.id === employeeId);
    setFormData({
      ...formData,
      employee_id: employeeId,
      employee_name: employee?.name || ""
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      present: "border-green-500/50 text-green-400 bg-green-500/10",
      absent: "border-red-500/50 text-red-400 bg-red-500/10",
      half_day: "border-yellow-500/50 text-yellow-400 bg-yellow-500/10",
      leave: "border-blue-500/50 text-blue-400 bg-blue-500/10"
    };
    return styles[status] || styles.present;
  };

  const getStatusIcon = (status) => {
    if (status === "present") return <UserCheck className="w-4 h-4 text-green-400" />;
    if (status === "absent") return <UserX className="w-4 h-4 text-red-400" />;
    return <Clock className="w-4 h-4 text-yellow-400" />;
  };

  // Get employees who haven't been marked for the selected date
  const unmarkedEmployees = employees.filter(
    emp => !attendance.some(a => a.employee_id === emp.id)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-primary animate-pulse font-heading text-xl">Loading attendance...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="attendance-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground flex items-center gap-3">
            <CalendarDays className="w-8 h-8 text-chart-3" />
            Staff Attendance
          </h1>
          <p className="text-muted-foreground mt-1">Track daily attendance of employees</p>
        </div>
        <div className="flex items-center gap-4">
          <Input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="bg-secondary/50 border-border w-48"
            data-testid="attendance-date-picker"
          />
          <Button 
            onClick={() => { resetForm(); setDialogOpen(true); }}
            className="glow-primary bg-primary hover:bg-primary/90"
            data-testid="mark-attendance-btn"
            disabled={unmarkedEmployees.length === 0}
          >
            <Plus className="w-4 h-4 mr-2" />
            Mark Attendance
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card className="border-border bg-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-muted-foreground text-sm">Present</p>
                <p className="font-heading text-2xl font-bold text-green-400">
                  {attendance.filter(a => a.status === "present").length}
                </p>
              </div>
              <UserCheck className="w-8 h-8 text-green-400/50" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-border bg-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-muted-foreground text-sm">Absent</p>
                <p className="font-heading text-2xl font-bold text-red-400">
                  {attendance.filter(a => a.status === "absent").length}
                </p>
              </div>
              <UserX className="w-8 h-8 text-red-400/50" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-border bg-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-muted-foreground text-sm">On Leave</p>
                <p className="font-heading text-2xl font-bold text-blue-400">
                  {attendance.filter(a => a.status === "leave").length}
                </p>
              </div>
              <CalendarDays className="w-8 h-8 text-blue-400/50" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-border bg-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-muted-foreground text-sm">Unmarked</p>
                <p className="font-heading text-2xl font-bold text-yellow-400">
                  {unmarkedEmployees.length}
                </p>
              </div>
              <Clock className="w-8 h-8 text-yellow-400/50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card className="border-border bg-card">
        <CardHeader className="border-b border-border/50">
          <CardTitle className="font-heading">
            Attendance for {new Date(selectedDate).toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Employee</TableHead>
                <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Check In</TableHead>
                <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Check Out</TableHead>
                <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Status</TableHead>
                <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Leave Type</TableHead>
                <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Remarks</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {attendance.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    No attendance records for this date. Mark attendance to get started.
                  </TableCell>
                </TableRow>
              ) : (
                attendance.map((record) => (
                  <TableRow key={record.id} className="border-border table-row-hover" data-testid={`attendance-row-${record.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getStatusIcon(record.status)}
                        <span className="font-medium text-foreground">{record.employee_name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-muted-foreground">{record.check_in || "-"}</TableCell>
                    <TableCell className="font-mono text-muted-foreground">{record.check_out || "-"}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={getStatusBadge(record.status)}>
                        {record.status.replace('_', ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground capitalize">{record.leave_type || "-"}</TableCell>
                    <TableCell className="text-muted-foreground">{record.remarks || "-"}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Mark Attendance Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Mark Attendance</DialogTitle>
            <DialogDescription>Mark attendance for {selectedDate}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="employee">Employee</Label>
              <Select value={formData.employee_id} onValueChange={handleEmployeeChange}>
                <SelectTrigger className="bg-secondary/50 border-border" data-testid="attendance-employee-select">
                  <SelectValue placeholder="Select employee" />
                </SelectTrigger>
                <SelectContent>
                  {unmarkedEmployees.map((employee) => (
                    <SelectItem key={employee.id} value={employee.id}>{employee.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                <SelectTrigger className="bg-secondary/50 border-border" data-testid="attendance-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="present">Present</SelectItem>
                  <SelectItem value="absent">Absent</SelectItem>
                  <SelectItem value="half_day">Half Day</SelectItem>
                  <SelectItem value="leave">Leave</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {formData.status === "leave" && (
              <div className="space-y-2">
                <Label htmlFor="leave_type">Leave Type</Label>
                <Select value={formData.leave_type} onValueChange={(v) => setFormData({ ...formData, leave_type: v })}>
                  <SelectTrigger className="bg-secondary/50 border-border" data-testid="attendance-leave-type-select">
                    <SelectValue placeholder="Select leave type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="annual">Annual Leave</SelectItem>
                    <SelectItem value="sick">Sick Leave</SelectItem>
                    <SelectItem value="casual">Casual Leave</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {(formData.status === "present" || formData.status === "half_day") && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="check_in">Check In</Label>
                  <Input
                    id="check_in"
                    type="time"
                    value={formData.check_in}
                    onChange={(e) => setFormData({ ...formData, check_in: e.target.value })}
                    className="bg-secondary/50 border-border"
                    data-testid="attendance-checkin-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="check_out">Check Out</Label>
                  <Input
                    id="check_out"
                    type="time"
                    value={formData.check_out}
                    onChange={(e) => setFormData({ ...formData, check_out: e.target.value })}
                    className="bg-secondary/50 border-border"
                    data-testid="attendance-checkout-input"
                  />
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="remarks">Remarks (Optional)</Label>
              <Input
                id="remarks"
                value={formData.remarks}
                onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                className="bg-secondary/50 border-border"
                placeholder="Any notes..."
                data-testid="attendance-remarks-input"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="glow-primary bg-primary" data-testid="attendance-submit-btn">
                Mark Attendance
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AttendancePage;
